import React from 'react';
import { Component, Position, Container, VBox, PuzzleInput, HBox } from "puzzle";
import Details from "../sections/Details/Details";
import Content from "../sections/Content/Content";
import CustomFields from "../sections/CustomFields/CustomFields";
import { every, find, includes, isEqual, map } from "lodash";
import { BATCH } from "../MultiBatchUpload/Types";
import { INSTRUCTIONS_DATA, VIEW_PROPS, VIEW_STATE, WORKFLOW } from "../Types";

class View extends Component<VIEW_PROPS, VIEW_STATE> {
  contentRef: any;
  customFieldsRef: any;
  detailsRef: any;

  initialSubmissionSnapshot: any;

  wrappersOfSections: any;
  staticWrappersOfSections: any;

  static defaultProps = {
    headerText: '',
    isTemplate: false,
  };
  public state: VIEW_STATE = {
    customFieldsVisible: false,
    mediaAssetsEnabled: false,
    selectedSection: 'details'
  };

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    this.staticWrappersOfSections = [
      { ref: this.detailsRef.detailsWrapperRef, name: 'details' },
      { ref: this.contentRef.contentWrapperRef, name: 'content' }];
    this.wrappersOfSections = [...this.staticWrappersOfSections];
  };


  /**
   * Check form validity
   */
  checkSubmissionValidity = () => this.props.form.isValid();

  /**
   * Validates submission if there are more then one batch. In that case every batch should be valid
   * including workflow and due date.
   */
  getContentValidity = () => {
    const contentState = this.contentRef?.state;
    let contentIsValid = true;
    if (contentState?.batchInfos?.length > 1) {
      const workflowsAreValid = this.validateWorkflows(contentState.batchInfos, contentState.workflows);
      let dueDatesAreValid = true;
      if (workflowsAreValid) {
        const targetFormatsPerBatch = map(contentState.batchInfos, 'targetLanguageInfos');
        let currentTargetFormats;
        let item = targetFormatsPerBatch.length;
        let mslaLevel = this.props.form.getData().mslaLevel;
        if (!includes(['TEMPLATE_EDIT', 'TEMPLATE_CREATE'], this.props.submissionMode) || (includes(['CREATE', 'CLONE'], this.props.submissionMode) && this.props.projectPolicies.hasEditDueDatePolicy)) {
          while (dueDatesAreValid && item) {
            item--;
            currentTargetFormats = targetFormatsPerBatch[item];
            dueDatesAreValid = mslaLevel ? true : every(map(currentTargetFormats, 'dueDate'));
          }
        }
      }
      contentIsValid = workflowsAreValid && dueDatesAreValid;
    }
    return contentIsValid;
  };

  /**
   * Hide custom fields tab item
   */
  hideCustomFields = () => this.setState({ customFieldsVisible: false });

  /**
   * Checks whether it has changed to its initial state
   */
  isDataChanged = (parsedData: any) => {
    let dataChanged = false;
    if (this.props.submissionMode === 'EDIT') {
      dataChanged = !isEqual(this.initialSubmissionSnapshot, parsedData);
    } else {
      dataChanged = !isEqual(this.initialSubmissionSnapshot, this.props.form.getData());
    }
    return dataChanged;
  };

  /**
   * Set form field values to initial setting
   */
  resetForm = () => {
    this.props.form.reset();
    this.dispatch('resetContentAndCustomFields', Position.end);
  };

  /**
   * Called when custom fields section gets new fields
   */
  setCustomFieldsVisible = (visibility: boolean) => {
    this.wrappersOfSections = visibility ?
      [...this.staticWrappersOfSections, { ref: this.customFieldsRef.customFieldsWrapperRef, name: 'customFields' }] :
      [...this.staticWrappersOfSections];
    this.setState({ customFieldsVisible: visibility });
  };

  /**
   * Saves initial submission data to local variable
   */
  getSubmissionSnapshot = () => this.initialSubmissionSnapshot;

  /**
   * Saves initial submission data to local variable
   */
  setSubmissionSnapshot = (submissionSnap: any) => {
    const { mediaAssetsNumber } = this.props.form.getFields();
    this.initialSubmissionSnapshot = {
      ...submissionSnap,
      mediaAssetsNumber: mediaAssetsNumber.value || ''
    };
  };

  /**
   * Saves initial submission data to local variable
   */
  takeSubmissionSnapshot = () => {
    if (this.props.submissionMode === 'EDIT') {
      this.dispatch('setInitDataForEdit', Position.end, {});
    } else {
      this.initialSubmissionSnapshot = this.props.form.getData();
    }
  };

  /**
   * Sets media assets visibility
   *
   * @param mediaAssetsEnabled
   */
  setMediaAssetsVisibility = (mediaAssetsEnabled: boolean) => {
    this.setState({
      mediaAssetsEnabled
    })
  };

  /**
   * Sets MAM ID & Instructions to form
   *
   * @param mediaAssetsNumber
   * @param instructions
   */
  setMediaAssetsAndInstructions = (mediaAssetsNumber: string, instructions: INSTRUCTIONS_DATA) => {
    const { instructionsHtmlText, notesHtmlText, referenceFiles } = instructions;
    this.props.form.setData({
      mediaAssetsNumber,
      instructions: instructionsHtmlText,
      notes: notesHtmlText,
      referenceFiles
    });
  }

  /**
   * Run global form validation
   */
  validateSubmission = () => {
    const { runFullValidation } = this.props.form;
    return runFullValidation() && this.getContentValidity();
  };

  /**
   * Check is workflow existing and valid
   * @param {BATCH[]} batches
   * @param {WORKFLOW[]} availableWorkflows
   */
  validateWorkflows = (batches: BATCH[], availableWorkflows: WORKFLOW[]) => {
    let isValid = true;
    let i = 0;
    let batchesLength = batches.length;
    let currentWorkflowId;
    while (i < batchesLength && isValid) {
      currentWorkflowId = batches[i].workflowId;
      if (currentWorkflowId) {
        isValid = !!find(availableWorkflows, { id: currentWorkflowId });
      } else {
        isValid = false;
      }
      i++;
    }

    return isValid;
  }

  renderBody() {
    const {
      fileUploadInProgress,
      form,
      isTemplate,
      submissionMode,
      submissionStatus,
      uploadFlowState,
      tptEnabled
    } = this.props;

    const {
      getFields,
      handleChange,
      handleBlur
    } = form;

    const { mediaAssetsNumber } = getFields();

    const { mediaAssetsEnabled } = this.state;
    const disableMAMIdField = !form.getData()?.uploadedFiles?.length

    return (
      <>
        <Container
          className={'tp-page-scrollable-area'}
          responsive={{ lg: 'k-vbox' }}
          displayFlex
          gap={4}
          id={'tp-submission-scrollable-area'}
        >
          <Details
            ref={component => this.detailsRef = component}
            form={form}
            isTemplate={isTemplate}
            projectPolicies={this.props.projectPolicies}
            submissionMode={submissionMode}
            tptEnabled={tptEnabled}
          />
          <VBox className={'tp-right-section'} gap={4}>
            <Content
              fileUploadInProgress={fileUploadInProgress}
              form={form}
              isTemplate={isTemplate}
              projectPolicies={this.props.projectPolicies}
              ref={component => this.contentRef = component}
              submissionMode={submissionMode}
              submissionStatus={submissionStatus}
              uploadFlowState={uploadFlowState}
            />

            {mediaAssetsEnabled && !isTemplate &&
              <Container
                className='tp-section-container tp-section-media-field tp-100'
                ariaLabel={this.translate('mediaAssetsTitle')}
                htmlTag={'section'}
                tabIndex={0}
              >
                <Container className={'tp-section-shadow'}>
                  <VBox gap={2} className={'tp-section-container-content'}>
                    <HBox>
                      <Container
                        htmlTag={'h3'}
                        hBox
                        alignItemsCenter
                        className="tp-section-container-title tp-100 tp-font-size-md"
                      >
                        {this.translate('mediaAssetsTitle')}
                      </Container>
                    </HBox>
                    <PuzzleInput
                      errorTxt={this.translateErrorMessage(mediaAssetsNumber)}
                      id='mediaAssetsNumber'
                      disabled={disableMAMIdField}
                      label={this.translate('mediaAssets')}
                      name='mediaAssetsNumber'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      withValidation
                      {...mediaAssetsNumber}
                    />
                  </VBox>
                </Container>
              </Container>}
            <CustomFields
              isTemplate={isTemplate}
              ref={component => this.customFieldsRef = component}
              form={form}
              setSectionVisible={this.setCustomFieldsVisible}
            />
          </VBox>
        </Container>
      </>
    );
  }
}

export default View;
